import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { MenuToggleIcon } from '@/components/ui/menu-toggle-icon';
import { useScroll } from '@/components/ui/use-scroll';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { LayoutDashboard, Loader2, LogOut, Rocket } from 'lucide-react';
import kinoraLogo from '@/assets/kinora_logo.svg';


export function Header() {
  const [open, setOpen] = React.useState(false);
  const [isSigningOut, setIsSigningOut] = React.useState(false);
  const scrolled = useScroll(10);
  const navigate = useNavigate();
  const { isAuthenticated, canAccessPublisherPortal, publisher } = useAuth();

  const isLoggedIn = isAuthenticated();
  const isOnboarded = canAccessPublisherPortal();
  const hasPublisherRecord = !!publisher;

  const handleSignOut = async () => {
    setIsSigningOut(true);
    await supabase.auth.signOut();
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/auth';
  };

  React.useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const renderDesktopAuth = () => {
    if (isLoggedIn && isOnboarded) {
      return (
        <>
          <button
            onClick={handleSignOut}
            disabled={isSigningOut}
            className="flex items-center gap-1.5 text-[13px] text-white/35 hover:text-white/65 transition-colors duration-150 disabled:opacity-50"
          >
            {isSigningOut
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <LogOut className="h-3.5 w-3.5" />}
            Sign Out
          </button>
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-[13px] font-medium text-white bg-publisher-500 hover:bg-publisher-400 transition-all duration-200 shadow-[0_0_18px_rgba(139,92,246,0.35)] hover:shadow-[0_0_26px_rgba(139,92,246,0.55)]"
          >
            <LayoutDashboard className="h-3.5 w-3.5" />
            Dashboard
          </button>
        </>
      );
    }
    if (isLoggedIn && !isOnboarded) {
      return (
        <>
          <button
            onClick={handleSignOut}
            disabled={isSigningOut}
            className="text-[13px] text-white/35 hover:text-white/65 transition-colors duration-150 disabled:opacity-50"
          >
            Sign Out
          </button>
          <button
            onClick={() => navigate(hasPublisherRecord ? '/onboarding' : '/select-partner-type')}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-[13px] font-medium text-white bg-publisher-500 hover:bg-publisher-400 transition-all duration-200 shadow-[0_0_18px_rgba(139,92,246,0.35)] hover:shadow-[0_0_26px_rgba(139,92,246,0.55)]"
          >
            <Rocket className="h-3.5 w-3.5" />
            {hasPublisherRecord ? 'Continue Setup' : 'Get Started'}
          </button>
        </>
      );
    }
    return (
      <>
        <button
          onClick={() => navigate('/auth')}
          className="text-[13px] text-white/45 hover:text-white/85 transition-colors duration-150"
        >
          Sign In
        </button>
        <button
          onClick={() => navigate('/auth?mode=signup')}
          className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-[13px] font-medium text-white bg-publisher-500 hover:bg-publisher-400 transition-all duration-200 shadow-[0_0_18px_rgba(139,92,246,0.30)] hover:shadow-[0_0_26px_rgba(139,92,246,0.50)]"
        >
          Get Started
        </button>
      </>
    );
  };

  const renderMobileAuth = () => {
    if (isLoggedIn && isOnboarded) {
      return (
        <div className="flex flex-col gap-2 pt-4 border-t border-white/[0.06]">
          <button
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-medium text-white bg-publisher-500 hover:bg-publisher-400 transition-colors"
            onClick={() => { setOpen(false); navigate('/dashboard'); }}
          >
            <LayoutDashboard className="h-4 w-4" />
            Dashboard
          </button>
          <button
            className="w-full py-2 text-sm text-white/35 hover:text-white/65 transition-colors"
            onClick={handleSignOut}
            disabled={isSigningOut}
          >
            Sign Out
          </button>
        </div>
      );
    }
    if (isLoggedIn && !isOnboarded) {
      return (
        <div className="flex flex-col gap-2 pt-4 border-t border-white/[0.06]">
          <button
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-medium text-white bg-publisher-500 hover:bg-publisher-400 transition-colors"
            onClick={() => { setOpen(false); navigate(hasPublisherRecord ? '/onboarding' : '/select-partner-type'); }}
          >
            <Rocket className="h-4 w-4" />
            {hasPublisherRecord ? 'Continue Setup' : 'Get Started'}
          </button>
          <button
            className="w-full py-2 text-sm text-white/35 hover:text-white/65 transition-colors"
            onClick={handleSignOut}
          >
            Sign Out
          </button>
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-2 pt-4 border-t border-white/[0.06]">
        <button
          className="w-full py-2.5 rounded-md text-sm font-medium text-white/65 border border-white/[0.08] hover:bg-white/[0.04] hover:text-white transition-colors"
          onClick={() => { setOpen(false); navigate('/auth'); }}
        >
          Sign In
        </button>
        <button
          className="w-full py-2.5 rounded-md text-sm font-medium text-white bg-publisher-500 hover:bg-publisher-400 transition-colors"
          onClick={() => { setOpen(false); navigate('/auth?mode=signup'); }}
        >
          Get Started
        </button>
      </div>
    );
  };

  return (
    <header
      className={cn(
        'sticky top-0 z-50 w-full transition-all duration-300',
        scrolled && 'border-b border-white/[0.05]',
      )}
      style={scrolled ? {
        backgroundColor: 'rgba(11, 18, 32, 0.82)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        boxShadow: '0 1px 0 0 rgba(139, 92, 246, 0.10), 0 4px 32px rgba(0, 0, 0, 0.4)',
      } : undefined}
    >
      <nav
        className="flex h-14 w-full items-center justify-between px-4 md:px-6"
        style={{ maxWidth: '64rem', marginLeft: 'auto', marginRight: 'auto' }}
      >
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 group">
          <img
            src={kinoraLogo}
            alt="Kinora"
            className="h-9 w-auto select-none md:h-10"
            draggable={false}
          />
          <span className="hidden sm:inline-flex items-center h-4 pl-1.5 border-l border-white/[0.10] text-[10px] font-medium text-white/25 uppercase tracking-[0.14em]">
            Studio
          </span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden items-center gap-5 md:flex">
          <div className="flex items-center gap-3">
            {renderDesktopAuth()}
          </div>
        </div>

        {/* Mobile toggle */}
        <button
          onClick={() => setOpen(!open)}
          className="md:hidden flex h-8 w-8 items-center justify-center rounded-md text-white/50 hover:text-white/80 hover:bg-white/[0.05] transition-colors"
          aria-expanded={open}
          aria-controls="mobile-menu"
          aria-label="Toggle menu"
        >
          <MenuToggleIcon open={open} className="size-5" duration={300} />
        </button>
      </nav>

      <MobileMenu open={open}>
        {renderMobileAuth()}
      </MobileMenu>
    </header>
  );
}

type MobileMenuProps = React.ComponentProps<'div'> & { open: boolean };

function MobileMenu({ open, children, className, ...props }: MobileMenuProps) {
  if (!open || typeof window === 'undefined') return null;

  return createPortal(
    <div
      id="mobile-menu"
      className="fixed top-14 right-0 bottom-0 left-0 z-40 md:hidden flex flex-col overflow-hidden"
      style={{
        backgroundColor: 'rgba(11, 18, 32, 0.96)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
      }}
    >
      <div
        data-slot={open ? 'open' : 'closed'}
        className={cn(
          'data-[slot=open]:animate-in data-[slot=open]:fade-in-0 data-[slot=open]:slide-in-from-top-2 duration-200',
          'flex flex-col gap-4 p-4 size-full',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
